import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { PageHeader, StatCard, inr } from "@/components/platform/platformUi";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
        return "—";
    }
}

const STATUS_STYLE = {
    paid: "bg-[#DCEEDC] text-[#1F7A55]",
    pending: "bg-[#FDF3DC] text-[#9A7B10]",
    failed: "bg-[#FEF5F3] text-[#C84B31]",
    refunded: "bg-[#E0EDFA] text-[#2E6BA8]",
};

export default function PlatformBilling() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [plans, setPlans] = useState([]);
    const [status, setStatus] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        tenant_id: "",
        plan_id: "",
        amount: "",
        billing_cycle: "monthly",
        status: "paid",
        payment_gateway: "razorpay",
        gateway_order_id: "",
        gateway_payment_id: "",
        period_days: "30",
        notes: "",
    });

    const load = async () => {
        try {
            const [bill, sum, t, p] = await Promise.all([
                api.get("/platform/billing", { params: { status } }),
                api.get("/platform/billing/summary"),
                api.get("/platform/tenants"),
                api.get("/platform/plans", { params: { active_only: true } }),
            ]);
            setRows(bill.data || []);
            setSummary(sum.data);
            setTenants(t.data || []);
            setPlans(p.data || []);
        } catch {
            toast.error("Could not load billing");
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const onPlanChange = (planId) => {
        const plan = plans.find((p) => p.id === planId);
        setForm((f) => ({
            ...f,
            plan_id: planId,
            amount: plan ? String(f.billing_cycle === "yearly" ? plan.price_yearly : plan.price_monthly) : f.amount,
            period_days: f.billing_cycle === "yearly" ? "365" : "30",
        }));
    };

    const record = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/platform/billing/record", {
                ...form,
                amount: Number(form.amount),
                period_days: Number(form.period_days),
            });
            toast.success("Billing record saved");
            setShowForm(false);
            setForm({
                tenant_id: "", plan_id: "", amount: "", billing_cycle: "monthly",
                status: "paid", payment_gateway: "razorpay", gateway_order_id: "",
                gateway_payment_id: "", period_days: "30", notes: "",
            });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Record failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="platform-billing-page">
            <PageHeader
                eyebrow="Billing"
                title="Subscription billing"
                subtitle="Salons pay via Razorpay on Billing — manual records for bank/offline payments"
                action={
                    <Button type="button" onClick={() => setShowForm(true)} className="bg-[#4A0E17] text-white hover:bg-[#3a0b12]">
                        <Plus className="mr-1.5 size-4" />
                        Record payment
                    </Button>
                }
            />

            {summary && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard label="MRR" value={inr(summary.mrr)} sub={`ARR ${inr(summary.arr)}`} />
                    <StatCard label="Revenue (30d)" value={inr(summary.platform_revenue_30d)} />
                    <StatCard label="All-time platform" value={inr(summary.platform_revenue_all_time)} />
                    <StatCard label="Pending / failed" value={`${summary.pending_invoices} / ${summary.failed_payments}`} />
                </div>
            )}

            {showForm && (
                <form onSubmit={record} className="space-y-4 rounded-xl border border-[#E8E4DE] bg-white p-5">
                    <p className="text-sm font-bold text-[#4A0E17]">Record subscription payment</p>
                    <p className="text-xs text-[#8A8178]">Use when a salon completes Razorpay checkout — gateway IDs optional for manual reconciliation</p>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Salon</Label>
                            <select className="h-9 w-full rounded-md border border-[#E8E4DE] px-3 text-sm" value={form.tenant_id} onChange={(e) => setForm((f) => ({ ...f, tenant_id: e.target.value }))} required>
                                <option value="">Select salon</option>
                                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Plan</Label>
                            <select className="h-9 w-full rounded-md border border-[#E8E4DE] px-3 text-sm" value={form.plan_id} onChange={(e) => onPlanChange(e.target.value)} required>
                                <option value="">Select plan</option>
                                {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Amount (₹)</Label>
                            <Input type="number" min={1} value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} required />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Cycle</Label>
                            <select className="h-9 w-full rounded-md border border-[#E8E4DE] px-3 text-sm" value={form.billing_cycle} onChange={(e) => setForm((f) => ({ ...f, billing_cycle: e.target.value, period_days: e.target.value === "yearly" ? "365" : "30" }))}>
                                <option value="monthly">Monthly</option>
                                <option value="yearly">Yearly</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Status</Label>
                            <select className="h-9 w-full rounded-md border border-[#E8E4DE] px-3 text-sm" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                                <option value="paid">Paid</option>
                                <option value="pending">Pending</option>
                                <option value="failed">Failed</option>
                                <option value="refunded">Refunded</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Gateway</Label>
                            <select className="h-9 w-full rounded-md border border-[#E8E4DE] px-3 text-sm" value={form.payment_gateway} onChange={(e) => setForm((f) => ({ ...f, payment_gateway: e.target.value }))}>
                                <option value="razorpay">Razorpay</option>
                                <option value="manual">Manual</option>
                                <option value="bank">Bank transfer</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Razorpay order ID</Label>
                            <Input value={form.gateway_order_id} onChange={(e) => setForm((f) => ({ ...f, gateway_order_id: e.target.value }))} placeholder="order_…" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Razorpay payment ID</Label>
                            <Input value={form.gateway_payment_id} onChange={(e) => setForm((f) => ({ ...f, gateway_payment_id: e.target.value }))} placeholder="pay_…" />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button type="submit" disabled={busy} className="bg-[#4A0E17] text-white">Save record</Button>
                        <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                    </div>
                </form>
            )}

            <div className="flex items-end gap-3">
                <div className="space-y-1">
                    <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Status filter</Label>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border border-[#E8E4DE] bg-white px-3 text-sm">
                        <option value="">All</option>
                        <option value="paid">Paid</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                    </select>
                </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-[#E8E4DE] bg-white">
                <table className="min-w-max w-full border-collapse">
                    <thead>
                        <tr className="border-b border-[#E8E4DE] text-left text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">
                            <th className="px-4 py-3">Date</th>
                            <th className="px-4 py-3">Salon</th>
                            <th className="px-4 py-3">Plan</th>
                            <th className="px-4 py-3">Amount</th>
                            <th className="px-4 py-3">Cycle</th>
                            <th className="px-4 py-3">Gateway</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Payment ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-b border-[#E8E4DE]/50 last:border-b-0">
                                <td className="px-4 py-3 text-sm text-[#8A8178]">{formatDate(r.paid_at || r.created_at)}</td>
                                <td className="px-4 py-3">
                                    <Link to={`/platform/salons/${r.tenant_id}`} className="font-semibold text-[#4A0E17] hover:text-[#C9A227]">
                                        {r.tenant?.name || r.tenant_id}
                                    </Link>
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-[#3D2817]">{r.plan?.name || "—"}</td>
                                <td className="px-4 py-3 text-sm font-bold text-[#4A0E17]">{inr(r.amount)}</td>
                                <td className="px-4 py-3 text-sm capitalize text-[#3D2817]">{r.billing_cycle}</td>
                                <td className="px-4 py-3 text-sm capitalize text-[#3D2817]">{r.payment_gateway}</td>
                                <td className="px-4 py-3">
                                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[r.status] || ""}`}>
                                        {r.status}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-xs font-mono text-[#8A8178]">{r.gateway_payment_id || "—"}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={8} className="px-4 py-10 text-center text-sm text-[#8A8178]">No billing records yet</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
